import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getProductos } from "../helpers/rutaProductos";
import logo from "../images/ridersmiami-logo.png";

const clean = (v) => String(v ?? "").trim();

const normalizarTexto = (value) => String(value || "").toUpperCase();

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const nombreCategoria = (categoria) => {
  if (!categoria) return "";

  if (typeof categoria === "string") return categoria;

  return (
    clean(categoria.nombre) ||
    clean(categoria.descripcion) ||
    clean(categoria.codigo) ||
    ""
  );
};

const codigoCategoria = (categoria) => {
  if (!categoria || typeof categoria !== "object") return "";

  return clean(categoria.codigo || categoria.codcategoria || "");
};

const grupoCategoria = (categoria) => {
  if (!categoria || typeof categoria !== "object") return "";

  return clean(categoria.grupoRecepcion || "");
};

const nombreMarca = (marca) => {
  if (!marca) return "";

  if (typeof marca === "string") return marca;

  return clean(marca.nombre || marca.marca || "");
};

const nombreProducto = (producto) => {
  return (
    clean(producto?.nombreComercial) ||
    clean(producto?.modelo) ||
    clean(producto?.descripcion) ||
    ""
  );
};

const coloresTexto = (producto) => {
  const colores = Array.isArray(producto?.coloresDisponibles)
    ? producto.coloresDisponibles
    : [];

  return (
    colores
      .map((c) => clean(c.nombre || c.color || c.descripcion))
      .filter(Boolean)
      .join(", ") || "-"
  );
};

const specsTexto = (producto) => {
  const specs = [];

  if (producto.tipoMotor && producto.tipoMotor !== "NO_APLICA") {
    specs.push(producto.tipoMotor);
  }

  if (Number(producto.cilindradaCC || 0) > 0) {
    specs.push(`${producto.cilindradaCC} CC`);
  }

  if (Number(producto.potenciaWatts || 0) > 0) {
    specs.push(`${producto.potenciaWatts} W`);
  }

  if (Number(producto.potenciaKw || 0) > 0) {
    specs.push(`${producto.potenciaKw} KW`);
  }

  if (Number(producto.tensionVolt || 0) > 0) {
    specs.push(`${producto.tensionVolt} V`);
  }

  if (Number(producto.capacidadAh || 0) > 0) {
    specs.push(`${producto.capacidadAh} AH`);
  }

  if (Number(producto.capacidadKwh || 0) > 0) {
    specs.push(`${producto.capacidadKwh} KWH`);
  }

  if (Number(producto.cantidadPaneles || 0) > 0) {
    specs.push(`${producto.cantidadPaneles} PANELES`);
  }

  if (Number(producto.potenciaPanelWatts || 0) > 0) {
    specs.push(`${producto.potenciaPanelWatts} W PANEL`);
  }

  return specs.join(" / ") || "-";
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const unwrapProductos = (resp) => {
  if (Array.isArray(resp?.productos)) return resp.productos;
  if (Array.isArray(resp?.data?.productos)) return resp.data.productos;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp)) return resp;

  return [];
};

const sortProductos = (a, b) => {
  const ca = nombreCategoria(a.categoria);
  const cb = nombreCategoria(b.categoria);

  const ma = nombreMarca(a.marca);
  const mb = nombreMarca(b.marca);

  const moA = clean(a.modelo);
  const moB = clean(b.modelo);

  return (
    ca.localeCompare(cb, "es") ||
    ma.localeCompare(mb, "es") ||
    moA.localeCompare(moB, "es")
  );
};

export default function ReporteProductos() {
  const [q, setQ] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [marcaFiltro, setMarcaFiltro] = useState("");
  const [tipoMotorFiltro, setTipoMotorFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [usaColorFiltro, setUsaColorFiltro] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [productos, setProductos] = useState([]);

  const cargarReporte = async () => {
    setLoading(true);
    setError("");

    try {
      const LIMIT = 200;
      let page = 1;
      let total = 0;
      let acumulado = [];

      do {
        const resp = await getProductos({
          page,
          limit: LIMIT,
          q: "",
        });

        const lote = unwrapProductos(resp);

        acumulado = acumulado.concat(lote);
        total = Number(resp?.total || lote.length || 0);
        page += 1;
      } while (acumulado.length < total);

      setProductos(acumulado.sort(sortProductos));
    } catch (err) {
      console.error("Error reporte productos:", err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar el reporte de productos"
      );

      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriasDisponibles = useMemo(() => {
    const map = new Map();

    productos.forEach((p) => {
      const id = getId(p.categoria) || nombreCategoria(p.categoria);
      const nombre = nombreCategoria(p.categoria);

      if (id && nombre && !map.has(id)) {
        map.set(id, nombre);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [productos]);

  const marcasDisponibles = useMemo(() => {
    const map = new Map();

    productos.forEach((p) => {
      const id = getId(p.marca) || nombreMarca(p.marca);
      const nombre = nombreMarca(p.marca);

      if (id && nombre && !map.has(id)) {
        map.set(id, nombre);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    const texto = clean(q).toUpperCase();

    return productos
      .filter((p) => {
        if (categoriaFiltro) {
          const idCat = getId(p.categoria) || nombreCategoria(p.categoria);
          if (idCat !== categoriaFiltro) return false;
        }

        if (marcaFiltro) {
          const idMarca = getId(p.marca) || nombreMarca(p.marca);
          if (idMarca !== marcaFiltro) return false;
        }

        if (tipoMotorFiltro && p.tipoMotor !== tipoMotorFiltro) {
          return false;
        }

        if (estadoFiltro === "ACTIVO" && p.activo === false) return false;
        if (estadoFiltro === "INACTIVO" && p.activo !== false) return false;

        if (usaColorFiltro === "SI" && !p.usaColor) return false;
        if (usaColorFiltro === "NO" && p.usaColor) return false;

        if (texto) {
          const cadena = [
            p.codprod,
            nombreCategoria(p.categoria),
            grupoCategoria(p.categoria),
            nombreMarca(p.marca),
            p.modelo,
            p.nombreComercial,
            p.descripcion,
            p.tipoMotor,
            p.politicaEnvio,
            p.aclaracion,
            p.observaciones,
            coloresTexto(p),
          ]
            .join(" ")
            .toUpperCase();

          if (!cadena.includes(texto)) return false;
        }

        return true;
      })
      .sort(sortProductos);
  }, [
    productos,
    q,
    categoriaFiltro,
    marcaFiltro,
    tipoMotorFiltro,
    estadoFiltro,
    usaColorFiltro,
  ]);

  const totales = useMemo(() => {
    return productosFiltrados.reduce(
      (acc, p) => {
        acc.cantidad += 1;

        if (p.activo === false) acc.inactivos += 1;
        else acc.activos += 1;

        if (p.usaColor) acc.conColor += 1;

        acc.totalPrecioVenta += Number(p.precioVentaUSD || 0);
        acc.totalPrecioMinimo += Number(p.precioMinimoUSD || 0);
        acc.totalComision += Number(p.comisionBaseUSD || 0);

        return acc;
      },
      {
        cantidad: 0,
        activos: 0,
        inactivos: 0,
        conColor: 0,
        totalPrecioVenta: 0,
        totalPrecioMinimo: 0,
        totalComision: 0,
      }
    );
  }, [productosFiltrados]);

  const limpiarFiltros = () => {
    setQ("");
    setCategoriaFiltro("");
    setMarcaFiltro("");
    setTipoMotorFiltro("");
    setEstadoFiltro("");
    setUsaColorFiltro("");
  };

  const generarPDF = async () => {
    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      const logoImg = await cargarImagen(logo);

      doc.addImage(logoImg, "PNG", 10, 8, 34, 12);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Reporte de Productos", 50, 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Riders Miami", 50, 20);

      doc.text(
        `Categoría: ${
          categoriasDisponibles.find((c) => c.id === categoriaFiltro)?.nombre ||
          "Todas"
        } | Marca: ${
          marcasDisponibles.find((m) => m.id === marcaFiltro)?.nombre ||
          "Todas"
        } | Tipo: ${tipoMotorFiltro || "Todos"} | Estado: ${
          estadoFiltro || "Todos"
        }`,
        287,
        18,
        { align: "right" }
      );

      doc.setDrawColor(180);
      doc.line(10, 24, 287, 24);

      const resumenY = 28;
      const boxW = 39;
      const boxH = 14;
      const gap = 3;

      const resumen = [
        { label: "Productos", value: String(totales.cantidad) },
        { label: "Activos", value: String(totales.activos) },
        { label: "Inactivos", value: String(totales.inactivos) },
        { label: "Con color", value: String(totales.conColor) },
        { label: "Precio venta", value: formatUSD(totales.totalPrecioVenta) },
        { label: "Precio mínimo", value: formatUSD(totales.totalPrecioMinimo) },
        { label: "Comisión", value: formatUSD(totales.totalComision) },
      ];

      resumen.forEach((item, i) => {
        const x = 6 + i * (boxW + gap);

        doc.setDrawColor(210);
        doc.roundedRect(x, resumenY, boxW, boxH, 1.5, 1.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.2);
        doc.text(item.label, x + 2, resumenY + 4.5);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.2);
        doc.text(String(item.value), x + 2, resumenY + 10);
      });

      const body = productosFiltrados.map((p) => [
        p.codprod || "",
        normalizarTexto(nombreCategoria(p.categoria)),
        normalizarTexto(grupoCategoria(p.categoria)),
        normalizarTexto(nombreMarca(p.marca)),
        normalizarTexto(p.modelo),
        normalizarTexto(nombreProducto(p)),
        normalizarTexto(specsTexto(p)),
        formatUSDPdf(p.precioVentaUSD),
        formatUSDPdf(p.precioMinimoUSD),
        formatUSDPdf(p.costoEnvioUSD),
        formatUSDPdf(p.comisionBaseUSD),
        p.usaColor ? "SI" : "NO",
        coloresTexto(p),
        p.activo === false ? "INACTIVO" : "ACTIVO",
      ]);

      autoTable(doc, {
        startY: 48,
        theme: "grid",
        margin: { left: 6, right: 6 },
        head: [
          [
            "Código",
            "Categoría",
            "Grupo",
            "Marca",
            "Modelo",
            "Producto",
            "Especificación",
            "Venta",
            "Mínimo",
            "Envío",
            "Comisión",
            "Color",
            "Colores",
            "Estado",
          ],
        ],
        body,
        foot: [
          [
            "Totales",
            "",
            "",
            "",
            "",
            "",
            "",
            formatUSDPdf(totales.totalPrecioVenta),
            formatUSDPdf(totales.totalPrecioMinimo),
            "",
            formatUSDPdf(totales.totalComision),
            "",
            "",
            "",
          ],
        ],
        styles: {
          fontSize: 5.8,
          cellPadding: 0.9,
          overflow: "linebreak",
          valign: "middle",
          minCellHeight: 5,
        },
        headStyles: {
          fillColor: [33, 37, 41],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 5.8,
          halign: "center",
        },
        footStyles: {
          fillColor: [245, 245, 245],
          textColor: 20,
          fontStyle: "bold",
          fontSize: 5.8,
        },
        columnStyles: {
          0: { cellWidth: 12, halign: "center" },
          1: { cellWidth: 24 },
          2: { cellWidth: 17 },
          3: { cellWidth: 18 },
          4: { cellWidth: 25 },
          5: { cellWidth: 31 },
          6: { cellWidth: 34 },
          7: { cellWidth: 18, halign: "right" },
          8: { cellWidth: 18, halign: "right" },
          9: { cellWidth: 15, halign: "right" },
          10: { cellWidth: 18, halign: "right" },
          11: { cellWidth: 10, halign: "center" },
          12: { cellWidth: 38 },
          13: { cellWidth: 16 },
        },
      });

      doc.save("reporte-productos-riders-miami.pdf");
    } catch (error) {
      console.error("Error generando PDF:", error);
      alert("No se pudo generar el PDF.");
    }
  };

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">Reporte de Productos</h4>
              <small className="text-muted">Riders Miami</small>
            </div>

            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={limpiarFiltros}>
                Limpiar
              </Button>

              <Button variant="dark" size="sm" onClick={cargarReporte} disabled={loading}>
                {loading ? "Buscando..." : "Buscar"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={loading || productosFiltrados.length === 0}
              >
                PDF
              </Button>
            </div>
          </div>

          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} sm={6} lg>
              <Form.Label>Buscar</Form.Label>
              <Form.Control
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Producto, modelo, código, categoría..."
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Categoría</Form.Label>
              <Form.Select
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todas</option>
                {categoriasDisponibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Marca</Form.Label>
              <Form.Select
                value={marcaFiltro}
                onChange={(e) => setMarcaFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todas</option>
                {marcasDisponibles.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Tipo motor</Form.Label>
              <Form.Select
                value={tipoMotorFiltro}
                onChange={(e) => setTipoMotorFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="COMBUSTION">COMBUSTIÓN</option>
                <option value="ELECTRICA">ELÉCTRICA</option>
                <option value="HIBRIDA">HÍBRIDA</option>
                <option value="NO_APLICA">NO APLICA</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="ACTIVO">ACTIVO</option>
                <option value="INACTIVO">INACTIVO</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Usa color</Form.Label>
              <Form.Select
                value={usaColorFiltro}
                onChange={(e) => setUsaColorFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="SI">SÍ</option>
                <option value="NO">NO</option>
              </Form.Select>
            </Col>
          </Row>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          <Row className="g-2 mb-3">
            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Productos</small>
                  <h5 className="mb-0">{totales.cantidad}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Activos</small>
                  <h5 className="mb-0">{totales.activos}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Inactivos</small>
                  <h5 className="mb-0">{totales.inactivos}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Con color</small>
                  <h5 className="mb-0">{totales.conColor}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Precio venta</small>
                  <h5 className="mb-0">{formatUSD(totales.totalPrecioVenta)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Precio mínimo</small>
                  <h5 className="mb-0">{formatUSD(totales.totalPrecioMinimo)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Comisión</small>
                  <h5 className="mb-0">{formatUSD(totales.totalComision)}</h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <Table striped bordered hover size="sm" className="align-middle">
              <thead
                className="table-dark"
                style={{ position: "sticky", top: 0, zIndex: 2 }}
              >
                <tr>
                  <th>Cód.</th>
                  <th>Categoría</th>
                  <th>Grupo</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Producto</th>
                  <th>Especificación</th>
                  <th className="text-end">Venta USD</th>
                  <th className="text-end">Mínimo USD</th>
                  <th className="text-end">Envío USD</th>
                  <th className="text-end">Comisión USD</th>
                  <th>Color</th>
                  <th>Colores</th>
                  <th>Política envío</th>
                  <th>Estado</th>
                  <th>Obs.</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="16" className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Cargando reporte...
                    </td>
                  </tr>
                ) : productosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="16" className="text-center py-4 text-muted">
                      No hay productos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  productosFiltrados.map((p) => (
                    <tr key={p._id || p.id || p.codprod}>
                      <td>{p.codprod || ""}</td>
                      <td>{normalizarTexto(nombreCategoria(p.categoria))}</td>
                      <td>{normalizarTexto(grupoCategoria(p.categoria))}</td>
                      <td>{normalizarTexto(nombreMarca(p.marca))}</td>
                      <td>{normalizarTexto(p.modelo)}</td>
                      <td>{normalizarTexto(nombreProducto(p))}</td>
                      <td>{normalizarTexto(specsTexto(p))}</td>
                      <td className="text-end fw-bold">{formatUSD(p.precioVentaUSD)}</td>
                      <td className="text-end">{formatUSD(p.precioMinimoUSD)}</td>
                      <td className="text-end">{formatUSD(p.costoEnvioUSD)}</td>
                      <td className="text-end">{formatUSD(p.comisionBaseUSD)}</td>
                      <td>{p.usaColor ? "SI" : "NO"}</td>
                      <td>{coloresTexto(p)}</td>
                      <td>{p.politicaEnvio || "NO_APLICA"}</td>
                      <td>{p.activo === false ? "INACTIVO" : "ACTIVO"}</td>
                      <td>{p.observaciones || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>

              {productosFiltrados.length > 0 && (
                <tfoot>
                  <tr className="reporte-productos-total-row">
                    <th colSpan="7">Totales</th>
                    <th className="text-end">{formatUSD(totales.totalPrecioVenta)}</th>
                    <th className="text-end">{formatUSD(totales.totalPrecioMinimo)}</th>
                    <th></th>
                    <th className="text-end">{formatUSD(totales.totalComision)}</th>
                    <th colSpan="5"></th>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}